import { http } from '@/plugins/http'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_DEPARTMENTS,
  RECEIVING_DEPARTMENTS,
  RECEIVE_DEPARTMENTS_METADATA,
  SAVING_DEPARTMENTS,
  SAVING_DEPARTMENTS_FAILURE,
  UPDATING_DEPARTMENTS,
  UPDATING_DEPARTMENTS_FAILURE,
  DELETE_DEPARTMENTS,
  DELETING_DEPARTMENTS,
  DELETING_DEPARTMENTS_FAILURE,
  CLEAR_DEPARTMENTS_STATE,
  RESET_DEPARTMENTS_META,
  CLEAR_STATE,
  INCREMENT_DEPARTMENTS_COUNT,
  RECEIVE_DEPARTMENTS_FAILURE
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const departmentSchema = new schema.Entity('departments')
const departmentListSchema = new schema.Array(departmentSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchDepartmentsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_DEPARTMENTS, true)
    return http
      .get(buildUrlWithQuery('departments', query, instanceUrl), { headers })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_DEPARTMENTS, false)
        this.commit(RESET_DEPARTMENTS_META)
        if (!r.hasOwnProperty('results')) return this.commit(RECEIVE_DEPARTMENTS_FAILURE, 'Error fetching departments')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, departmentListSchema)
        this.commit(RECEIVE_DEPARTMENTS, { data: entities.departments })
        this.commit(RECEIVE_DEPARTMENTS_METADATA, {next, previous, count, result: result.length})
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_DEPARTMENTS, false)
        this.commit(RECEIVE_DEPARTMENTS_FAILURE, handleXHRError(e))
      })
  },
  fetchDepartmentsOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_DEPARTMENTS, true)
    return http({
      url: buildUrl('departments', query || null, instanceUrl),
      headers,
      method: 'options'
    })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_DEPARTMENTS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities } = normalize(choices, departmentListSchema)
          this.commit(RECEIVE_DEPARTMENTS, { data: entities.departments })
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_DEPARTMENTS, false)
        this.commit(RECEIVE_DEPARTMENTS_FAILURE, handleXHRError(e))
      })
  },
  deleteDepartment (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    if (payload) {
      this.commit(DELETING_DEPARTMENTS, true)
      this.commit(DELETING_DEPARTMENTS_FAILURE, { id: '', err: '' })
      return http({
        url: buildUrl('departments/bulk_delete', null, instanceUrl),
        body: payload,
        headers: headers,
        method: 'DELETE'
      })
        .then(checkStatus)
        .then(() => {
          this.commit(DELETE_DEPARTMENTS, payload)
          this.commit(DELETING_DEPARTMENTS, false)
        })
        .catch(e => {
          this.commit(DELETING_DEPARTMENTS_FAILURE, { id: payload, err: e || 'Error deleting vendor' })
        })
    } else {
      this.commit(DELETING_DEPARTMENTS, false)
    }
  }
}

const getters = {
  getDepartments: state => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllDepartments: state => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getDepartmentMetadata: state => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getDepartmentById: state => id => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getDefaultDepartment: state => {
    return state.cache ? Object.values(state.cache).find(v => v.active) : {}
  },
  getDepartmentFetchStatus: state => {
    return state.receivingData
  },
  getDepartmentSaveStatus: state => {
    return state.saving
  },
  getDepartmentUpdateStatus: state => {
    return state.updating
  },
  getDepartmentDeleteStatus: state => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_DEPARTMENTS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_DEPARTMENTS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_DEPARTMENTS_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_DEPARTMENTS] (state, payload) {
    state.saving = payload
  },
  [SAVING_DEPARTMENTS_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_DEPARTMENTS] (state, payload) {
    state.updating = payload
  },
  [UPDATING_DEPARTMENTS_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_DEPARTMENTS] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_DEPARTMENTS] (state, payload) {
    state.deleting = payload
  },
  [DELETING_DEPARTMENTS_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_DEPARTMENTS_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_DEPARTMENTS_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_DEPARTMENTS_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_DEPARTMENTS_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}
